import { useRef, useEffect } from 'react'
import { FormControl, FormLabel, Input, Textarea, Checkbox, Stack, Button, FormHelperText, useToast } from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useMutation, gql } from '@apollo/client'
import { useHotkeys } from 'react-hotkeys-hook'
import hotkeys from 'src/lib/hotkeys'
import { motion } from 'framer-motion'

const MotionButton = motion(Button)

const UPDATE_SETTINGS = gql`
  mutation UpdateSettingsMutation($input: UpdateSettingsInput!) {
    updateSettings(input: $input) {
      message
      maintenance
      min_version
      latest_version
      force_update
      change_log
    }
  }
`

const SettingsForm = ({ settings, onSave = () => { } }) => {
  const inputRef = useRef()
  const toast = useToast()
  const {
    handleSubmit,
    getValues,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      message: settings?.message ?? '',
      maintenance: settings?.maintenance ?? false,
      min_version: settings?.min_version ?? '',
      latest_version: settings?.latest_version ?? '',
      force_update: settings?.force_update ?? false,
      change_log: settings?.change_log ?? '',
    },
  })

  const [update, { loading }] = useMutation(UPDATE_SETTINGS, {
    onCompleted: () => {
      toast({
        title: 'Settings saved successfully',
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top',
      })
      onSave()
    },
  })

  const onSubmit = (params) => {
    console.log(params);
    update({
      variables: {
        input: {
          message: params.message ?? '',
          maintenance: Boolean(params.maintenance),
          min_version: params.min_version ?? '',
          latest_version: params.latest_version ?? '',
          force_update: Boolean(params.force_update),
          change_log: params.change_log ?? '',
        },
      },
    })
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  })

  useHotkeys(
    hotkeys.saveEdit,
    (e) => {
      e.preventDefault()
      const values = getValues()
      console.log(values);
      onSubmit({
        message: getValues('message'),
        maintenance: getValues('maintenance'),
        min_version: getValues('min_version'),
        latest_version: getValues('latest_version'),
        force_update: getValues('force_update'),
        change_log: getValues('change_log'),
      })
    },
    { filterPreventDefault: true }
  )

  if (!settings) {
    return null
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <FormControl isInvalid={!!errors.message}>
          <FormLabel>Message</FormLabel>
          <Textarea
            size="lg"
            placeholder="Maintenance message"
            {...register('message', { required: true })}
          />
        </FormControl>
        <FormControl>
          <Checkbox
            defaultIsChecked={settings.maintenance}
            {...register('maintenance')}
          >
            Maintenance mode
          </Checkbox>
          <FormHelperText id="maintenance-helper-text">
            Enable maintenance mode to show a message to users.
          </FormHelperText>
        </FormControl>
        <FormControl isInvalid={!!errors.min_version}>
          <FormLabel>Minimum version</FormLabel>
          <Input
            size="lg"
            placeholder="Minimum version"
            {...register('min_version', {
              validate: (value, formValues) => {
                if (!formValues.force_update) return true
                return (
                  (value != null && String(value).trim() !== '') ||
                  'Required when force update is enabled'
                )
              },
            })}
          />
          <FormHelperText id="min-version-helper-text">
            Minimum supported app version that users must update to.
          </FormHelperText>
        </FormControl>
        <FormControl isInvalid={!!errors.latest_version}>
          <FormLabel>Latest version</FormLabel>
          <Input
            size="lg"
            placeholder="Latest version"
            {...register('latest_version', {
              validate: (value, formValues) => {
                if (!formValues.force_update) return true
                return (
                  (value != null && String(value).trim() !== '') ||
                  'Required when force update is enabled'
                )
              },
            })}
          />
          <FormHelperText id="latest-version-helper-text">
            Latest app version that users can update to.
          </FormHelperText>
        </FormControl>
        <FormControl isInvalid={!!errors.change_log}>
          <FormLabel>Change log</FormLabel>
          <Textarea
            size="lg"
            placeholder="Change log"
            {...register('change_log')}
          />
          <FormHelperText id="change-log-helper-text">
            Change log for the new version.
          </FormHelperText>
        </FormControl>
        <FormControl>
          <Checkbox
            defaultIsChecked={settings.force_update}
            {...register('force_update')}
          >
            Enable force update to latest version
          </Checkbox>
          <FormHelperText id="force-update-helper-text">
            If breaking changes in new version, show update now button to users to update to latest version.
          </FormHelperText>
        </FormControl>
        <MotionButton
          colorScheme="teal"
          isLoading={loading}
          loadingText="Please wait"
          whileTap={{ scale: 0.9 }}
          type="submit"
        >
          Save
        </MotionButton>
      </Stack>
    </form>
  )
}

export default SettingsForm
